/**
 * Task status presentation shared by the detail panels and the deleted-tasks
 * dialog. Lives apart from any one component so a consumer needing two lookups
 * does not pull a whole panel's import graph along with them.
 */

import type { TaskStatus } from '../../services/api/types.gen';

export const STATUS_DISPLAY_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  saved: 'Saved',
  returned_for_review: 'Returned for Review',
  submitted_with_signature: 'Submitted (Signed)',
  submitted: 'Submitted',
  pending_clinician_signature: 'Pending Clinician Signature',
  completed: 'Completed',
  exported: 'Exported',
  reopened: 'Reopened',
  sent_to_physician: 'Sent to Physician',
  returned_with_signature: 'Returned with Signature',
  sent_no_signature_required: 'Completed',
};

/**
 * A missed visit that is still being documented, as Kinnser writes it: the
 * ordinary status with a marker appended. Kinnser has no status meaning
 * "missed", it keeps the task on its rung and marks the rung, which is the
 * same shape as a Roger visit carrying missed_visit_data while its status runs
 * the normal ladder. Suffixing rather than replacing is what keeps an
 * unfinished missed visit distinguishable from a finished one.
 */
const MISSED_STATUS_SUFFIX = ' (MV)';

/**
 * A missed visit whose documentation is finished but which nobody has
 * approved. Kinnser shows this from the moment the report is submitted until a
 * case manager approves it.
 *
 * Kinnser writes it "Missed Visit (MV)", naming the status and then appending
 * the marker it puts on every missed rung. The marker is dropped here: the
 * status already says missed, and the suffix only earns its place on the rungs
 * whose own wording does not say it.
 */
const MISSED_VISIT_TERMINAL_LABEL = 'Missed Visit';

/**
 * A missed visit whose report has been approved. Approval is its own act
 * against the missed-visit record, not a consequence of the chart being
 * finished, so this is used only when that flag is actually set. Claiming it
 * from the status alone would contradict the Approved row the missed-visit
 * form prints on the same screen.
 */
const MISSED_VISIT_APPROVED_LABEL = 'Missed Visit (Approved)';

/**
 * The statuses that mean the chart is finished. Mirrors COMPLETED_STATUSES in
 * backend/app/models/enum/task.py.
 *
 * Both collapse to one missed-visit label rather than keeping the
 * Completed/Exported distinction the ordinary labels draw, because a missed
 * visit is held out of the OASIS export entirely and so has no business
 * reaching Exported in the first place.
 */
const TERMINAL_STATUSES = new Set<string>(['completed', 'exported']);

/**
 * What the label needs to know about a visit's missed-visit report.
 *
 * Stated outright rather than inferred from whether a record object is
 * present, because the two surfaces hold different shapes: the chart has the
 * record itself, the queue has a pair of flags. Leaving it to truthiness made
 * `{}` mean "missed, unapproved" and `null` mean "not missed", which no type
 * enforced.
 */
export interface MissedVisitLabelSource {
  isMissed: boolean;
  approved?: boolean | null;
}

/**
 * Read the label's view of a missed-visit report off a response that carries
 * the record itself, where the record's presence is what makes it missed.
 */
const missedVisitLabelSource = (
  missedVisitData?: { approved?: boolean | null } | null
): MissedVisitLabelSource => ({
  isMissed: Boolean(missedVisitData),
  approved: missedVisitData?.approved ?? false,
});

export function getStatusBadgeVariant(
  status: TaskStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'completed': // fallthrough
    case 'exported':
      return 'default';
    case 'submitted_with_signature':
    case 'pending_clinician_signature':
      return 'secondary';
    case 'returned_for_review':
      return 'destructive';
    case 'not_started':
    case 'saved':
    case 'submitted':
    case 'reopened':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Apply the missed-visit vocabulary to an already-resolved status label.
 *
 * Split out from `getStatusLabel` so a surface that keeps its own wording for
 * some statuses, as the Hotbox queue does, can still render a missed visit the
 * way every other surface does, from one copy of the rule.
 */
export const withMissedVisitLabel = (
  status: string,
  label: string,
  missed?: MissedVisitLabelSource | null
): string => {
  if (!missed?.isMissed) {
    return label;
  }
  if (TERMINAL_STATUSES.has(status)) {
    return missed.approved === true
      ? MISSED_VISIT_APPROVED_LABEL
      : MISSED_VISIT_TERMINAL_LABEL;
  }
  return `${label}${MISSED_STATUS_SUFFIX}`;
};

/**
 * Human label for a status, falling back to Title Case of the raw value.
 *
 * Takes `string` so a status the generated types do not know about yet still
 * renders readably instead of falling through the map as a raw enum value.
 *
 * Passing the visit's missed-visit record renders the status in the vocabulary
 * agencies already read in Kinnser, so a finished missed visit says so rather
 * than reporting the "Completed" its status alone would give. The record's
 * presence is what makes a visit missed, and its `approved` flag is what
 * separates Kinnser's two finished states.
 */
export const getStatusLabel = (
  status: string,
  missedVisitData?: { approved?: boolean | null } | null
): string =>
  withMissedVisitLabel(
    status,
    STATUS_DISPLAY_LABELS[status] ??
      status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase()),
    missedVisitLabelSource(missedVisitData)
  );

/**
 * The status a chart row shows for a visit.
 *
 * Completing an evaluation raises a separate order for the physician to sign,
 * and Order Manager moves that order, not the visit. Once the chart is
 * finished the only thing left to happen to it is the signature, so the row
 * reads as that order would on its own row: Completed until it is sent, then
 * Sent to Physician, then Returned with Signature. That is also how WellSky
 * shows an evaluation.
 *
 * Only a finished chart takes the order's status. A reopened evaluation is
 * being revised, and whatever the physician signed no longer describes it. A
 * missed visit keeps its own status too, because the missed-visit wording is
 * what tells it apart from a real one.
 *
 * Only the two statuses that mean the order went to the physician replace the
 * visit's. Nothing stops a direct edit from putting the order in some other
 * status, and a finished evaluation must never read as Reopened or Saved
 * because of it.
 */
const SIGNATURE_REQUEST_ROW_STATUSES = new Set<TaskStatus>([
  'sent_to_physician',
  'returned_with_signature',
]);

export const getChartRowStatus = (
  visitStatus: TaskStatus,
  signatureRequestStatus?: TaskStatus | null,
  isMissed = false
): TaskStatus =>
  signatureRequestStatus &&
  SIGNATURE_REQUEST_ROW_STATUSES.has(signatureRequestStatus) &&
  !isMissed &&
  TERMINAL_STATUSES.has(visitStatus)
    ? signatureRequestStatus
    : visitStatus;
